import React from 'react';
import { useDragAndDrop } from '#hooks/use-drag-and-drop.js';

export function usePlannerDragAndDrop({
    days,
    plans,
    total,
    moveDay,
    movePlan,
    addNewDay,
    selectedDayIndex,
    setSelectedDayIndex,
    selectedItemId,
    setSelectedItemId
}) {
    const { draggedItemId, onDragStart, onDragEnd, onDragCancel, sensors } = useDragAndDrop({
        onDragEnd: (active, over) => {
            switch (true) {
                case !over || active.id === over.id:
                    break;

                case active.id.startsWith('day') && over.id.startsWith('day'): {
                    const from = dayIds.indexOf(active.id);
                    const to = dayIds.indexOf(over.id);
                    moveDay(from, to);
                    if (from === selectedDayIndex) setSelectedDayIndex(to);
                    break;
                }

                case active.id.startsWith('day') && !over.id.startsWith('day'): {
                    const from = dayIds.indexOf(active.id);
                    const to = plans.find((plan) => plan.id === over.id).dayIndex;
                    moveDay(from, to);
                    if (from === selectedDayIndex) setSelectedDayIndex(to);
                    break;
                }

                case !active.id.startsWith('day') && over.id.startsWith('add'): {
                    addNewDay();
                    const activePlan = plans.find((plan) => plan.id === active.id);
                    const fromPlanIndex = activePlan.planIndex;
                    const from = [activePlan.dayIndex, fromPlanIndex];
                    const to = [total, 0];
                    movePlan(from, to);
                    if (fromPlanIndex === Number(selectedItemId)) setSelectedItemId(0);
                    break;
                }

                case !active.id.startsWith('day') && over.id.startsWith('day'): {
                    const activePlan = plans.find((plan) => plan.id === active.id);
                    const fromPlanIndex = activePlan.planIndex;
                    const fromDayIndex = activePlan.dayIndex;
                    const toDayIndex = dayIds.indexOf(over.id);

                    const from = [fromDayIndex, fromPlanIndex];
                    const to = [toDayIndex];
                    movePlan(from, to);
                    if (fromPlanIndex === Number(selectedItemId)) {
                        const lastIndex = days[to].plans.length;
                        setSelectedItemId(lastIndex);
                    }
                    setSelectedDayIndex(toDayIndex);
                    break;
                }

                case !active.id.startsWith('day') && !over.id.startsWith('day'): {
                    const activePlan = plans.find((plan) => plan.id === active.id);
                    const overPlan = plans.find((plan) => plan.id === over.id);
                    const fromPlanIndex = activePlan.planIndex;
                    const toPlanIndex = overPlan.planIndex;
                    const from = [activePlan.dayIndex, fromPlanIndex];
                    const to = [overPlan.dayIndex, toPlanIndex];
                    movePlan(from, to);
                    if (fromPlanIndex === Number(selectedItemId)) setSelectedItemId(toPlanIndex);
                    break;
                }
            }
        }
    });

    const draggedDay = React.useMemo(() => {
        if (!draggedItemId || !draggedItemId.startsWith('day')) return;
        const index = days.findIndex((day) => day.id === draggedItemId);
        const data = days.at(index);
        return { index, data };
    }, [days, draggedItemId]);

    const draggedPlan = React.useMemo(() => {
        if (!draggedItemId || draggedItemId.startsWith('day')) return;

        const planDay = plans.find((plan) => plan.id === draggedItemId).dayIndex;

        const index = days[planDay].plans.findIndex((day) => day.id === draggedItemId);
        const data = days[planDay].plans.at(index);
        return { index, data };
    }, [days, draggedItemId, plans]);

    const dayIds = React.useMemo(() => days?.map((o) => o.id) ?? [], [days]);

    return {
        dayIds,
        draggedDay,
        draggedPlan,
        onDragStart,
        onDragEnd,
        onDragCancel
    };
}
